import { format } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { useTable } from 'react-table';
import { Input, Select as MaterialSelect, Button, TextField, Tooltip } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import Select from 'react-select';
import {
  BiomarkerData,
  UserSubmittedLabResultsDetailsResponse,
  UserSubmittedLabResultsRequest,
} from '../../../interfaces/user-submitted-lab-result';
import useStyles from '../routes/user-submitted-lab-results-details/UserSubmittedLabResultDetails.styles';
import {
  selectCurrentUserSubmittedLabResult,
  setAutomatedStatusChange,
  setSavingSuccessful,
  setShouldRedirect,
  updateUserSubmittedLabResultById,
} from '../state';
import UserSubmittedLabResultHeader from './UserSubmittedLabResultHeader';
import UserSubmittedLabResultRejectDialog from './UserSubmittedLabResultRejectDialog';
import UserSubmittedLabResultConfirmDialog from './UserSubmittedLabResultConfirmDialog';
import { selectLoggedIn } from '../../auth/state';

interface UserSubmittedLabResultsTableProps {
  biomarkerData: BiomarkerData;
  userSubmittedLabResult: UserSubmittedLabResultsDetailsResponse;
}

function formatDate(date: string | undefined) {
  if (date) {
    try {
      return format(new Date(date), 'yyyy-MM-dd');
    } catch (e) {
      return date;
    }
  }
  return date;
}

const UserSubmittedLabResultsTable: React.FC<UserSubmittedLabResultsTableProps> = ({
  biomarkerData,
  userSubmittedLabResult,
}) => {
  const classes = useStyles();
  const loggedIn = useSelector(selectLoggedIn);
  const [formSubmittedBy, setFormSubmittedBy] = useState(userSubmittedLabResult.formSubmittedBy);
  const [submitConfirmDialogOpen, setSubmitConfirmDialogOpen] = useState(false);
  const [completeConfirmDialogOpen, setCompleteConfirmDialogOpen] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [rowToDeleteIndex, setRowToDeleteIndex] = useState<number | null>(null);
  const [biomarkerNameToDelete, setBiomarkerNameToDelete] = useState<string>('');
  const [selectedBiomarker, setSelectedBiomarker] = useState('');
  const [data, setData] = useState<any[]>([]);
  const dispatch = useDispatch();
  const { userId, resultId } = useParams<{ userId: string; resultId: string }>();
  const [status, setStatus] = useState(userSubmittedLabResult.status);
  const [laboratoryName, setLaboratoryName] = useState<string>(
    userSubmittedLabResult.laboratoryName ?? '',
  );
  const [sampleDate, setSampleDate] = useState<string | undefined>(
    userSubmittedLabResult.sampleDate
      ? format(new Date(userSubmittedLabResult.sampleDate), 'yyyy-MM-dd')
      : undefined,
  );
  const [isValueValid, setIsValueValid] = useState(true);
  const [isFromValid, setIsFromValid] = useState(true);
  const [isToValid, setIsToValid] = useState(true);
  const [isDateValid, setIsDateValid] = useState(true);
  const [isLabNameValid, setIsLabNameValid] = useState(true);
  const [isUnitLaboratoryValid, setIsUnitLaboratoryValid] = useState(true);
  const history = useHistory();
  const areAllRowsValid = data.every(row => row.isValid);

  const [isFormValid, setIsFormValid] = useState<boolean>(
    isLabNameValid &&
      isDateValid &&
      isToValid &&
      isFromValid &&
      isValueValid &&
      isUnitLaboratoryValid &&
      areAllRowsValid,
  );

  useEffect(() => {
    const areAllRowsValid = data.every(row => row.isValid);
    setIsFormValid(
      isLabNameValid &&
        isDateValid &&
        isToValid &&
        isFromValid &&
        isValueValid &&
        isUnitLaboratoryValid &&
        areAllRowsValid,
    );
  }, [
    isLabNameValid,
    isDateValid,
    isToValid,
    isFromValid,
    isValueValid,
    isUnitLaboratoryValid,
    data,
  ]);

  useEffect(() => {
    const areAllRowsValid = data.every(row => row.isValid);
    if (
      userSubmittedLabResult.status === 'inProgress' ||
      userSubmittedLabResult.status === 'inReview'
    ) {
      setIsFormValid(
        isLabNameValid &&
          isDateValid &&
          isToValid &&
          isFromValid &&
          isValueValid &&
          isUnitLaboratoryValid &&
          areAllRowsValid,
      );
    }
  }, []);

  const getTooltipContent = (biomarkerName: string) => {
    const extractedBiomarkerCode =
      biomarkerName
        .match(/\(([^()]*?)\)(?![^\[]*\])/g)
        ?.pop()
        ?.slice(1, -1) || '';
    const biomarker = biomarkerData.biomarkers[extractedBiomarkerCode];
    if (biomarker?.alternateNames && biomarker.alternateNames.length > 0) {
      return biomarker.alternateNames.join(',\r\n');
    } else {
      return 'No alternate names available.';
    }
  };

  const handleOpenPDF = () => {
    if (userSubmittedLabResult.resultFileURL) {
      window.open(userSubmittedLabResult.resultFileURL, '_blank');
    }
  };

  const handleDeleteRow = (row: any) => {
    const biomarkerName = row.original.biomarker;
    setRowToDeleteIndex(row.index);
    setShowDeleteConfirmation(true);
    setBiomarkerNameToDelete(biomarkerName);
  };

  interface Row {
    original: {
      biomarker: string;
      unitHumanity: string;
      rangeHumanity: string;
      value: string;
      unitLaboratory: string;
      rangeLaboratory: {
        from?: string;
        to?: string;
      };
    };
  }

  const handleSave = async (
    newStatus?: string,
    rejectedReason?: string,
    rejectedReasonTitle?: string,
    rejectedComment?: string,
  ) => {
    dispatch(setSavingSuccessful(false));
    dispatch(setAutomatedStatusChange(false));
    dispatch(setShouldRedirect(true));
    const formattedResults = data.map((row, index) => ({
      id: index + 1,
      code: row.biomarker
        .match(/\(([^()]*?)\)(?![^\[]*\])/g)
        ?.pop()
        ?.slice(1, -1),
      value: row.value !== '' && row.value !== null ? Number(row.value) : null,
      unit: row.unitLaboratory?.props?.value ?? row.unitLaboratory,
      range: {
        from: row.rangeLaboratory.from ? Number(row.rangeLaboratory.from) : undefined,
        to: row.rangeLaboratory.to ? Number(row.rangeLaboratory.to) : undefined,
      },
    }));

    const savedData: UserSubmittedLabResultsRequest = {
      status: newStatus || status,
      ...(laboratoryName && { laboratoryName }),
      sampleDate: formatDate(sampleDate),
      rejectedReason,
      rejectedReasonTitle,
      rejectedComment,
      items: formattedResults,
    };

    await dispatch(updateUserSubmittedLabResultById(userId, resultId, savedData));
  };

  const canSubmitForReview = useMemo(() => {
    return status === 'inProgress' && isFormValid && data.length > 0;
  }, [status, data, isFormValid]);

  const formSubmittedByIsLoggedIn = useMemo(() => {
    return formSubmittedBy === loggedIn?.payload.username;
  }, [formSubmittedBy, loggedIn]);

  const canComplete = useMemo(() => {
    return status === 'inReview' && isFormValid && data.length > 0;
  }, [status, data, isFormValid]);

  const handleConfirmSubmit = () => {
    setSubmitConfirmDialogOpen(true);
  };

  const handleConfirmComplete = () => {
    setCompleteConfirmDialogOpen(true);
  };

  const confirmDeleteRow = (index: number) => {
    const dataCopy = [...data];
    dataCopy.splice(index, 1);
    setData(dataCopy);
  };

  const columns: any[] = React.useMemo(
    () => [
      { Header: 'Biomarker', accessor: 'biomarker' },
      { Header: 'Unit (Humanity)', accessor: 'unitHumanity' },
      { Header: 'Range (Humanity)', accessor: 'rangeHumanity' },
      {
        Header: 'Value (Laboratory)',
        accessor: 'value',
        Cell: ({ cell, row }: { cell: any; row: Row }) => (
          <EditableCell
            cell={cell}
            row={row}
            biomarkerData={biomarkerData}
            setIsFromValid={setIsFromValid}
            setIsToValid={setIsToValid}
            setIsValueValid={setIsValueValid}
          />
        ),
      },
      {
        Header: 'Unit (Laboratory)',
        accessor: 'unitLaboratory',
        Cell: ({ row }: { row: any }) => (
          <MaterialSelect
            native
            className={classes.dropdown}
            value={row.values.unitLaboratory}
            onChange={e => {
              const selectedUnit = e.target.value;
              row.original.unitLaboratory = selectedUnit;
              if (selectedUnit) {
                setIsUnitLaboratoryValid(true);
              } else {
                setIsUnitLaboratoryValid(false);
              }
              setData(prevData => [...prevData]);
            }}
            input={<Input id="demo-dialog-native" />}
            variant="filled"
            disabled={
              userSubmittedLabResult.status === 'completed' ||
              userSubmittedLabResult.status === 'rejected'
            }
            defaultValue=""
            required
          >
            <option value="" disabled>
              Select Unit...
            </option>
            {biomarkerData.biomarkers[
              row.original.biomarker
                .match(/\(([^()]*?)\)(?![^\[]*\])/g)
                ?.pop()
                ?.slice(1, -1)
            ].allUnits.map(unitData => (
              <option key={unitData.unit} value={unitData.unit}>
                {unitData.unit}
              </option>
            ))}
          </MaterialSelect>
        ),
      },
      {
        Header: 'Range (Laboratory)',
        accessor: 'rangeLaboratory',
        Cell: ({ cell, row }: { cell: any; row: Row }) => (
          <EditableCell
            cell={cell}
            row={row}
            biomarkerData={biomarkerData}
            setIsValueValid={setIsValueValid}
            setIsFromValid={setIsFromValid}
            setIsToValid={setIsToValid}
          />
        ),
      },
      {
        Header: 'Delete',
        accessor: 'delete',
        Cell: ({ row }: any) => (
          <Button
            onClick={() => handleDeleteRow(row)}
            tabIndex={-1}
            disabled={
              userSubmittedLabResult.status === 'completed' ||
              userSubmittedLabResult.status === 'rejected'
            }
          >
            <DeleteIcon />
          </Button>
        ),
      },
    ],
    [classes.dropdown, biomarkerData],
  );

  const handleAddRowOnSelect = (selectedBiomarker: string) => {
    const selectedBiomarkerCode =
      selectedBiomarker
        .match(/\(([^()]*?)\)(?![^\[]*\])/g)
        ?.pop()
        ?.slice(1, -1) || '';

    if (selectedBiomarkerCode) {
      const biomarker = Object.values(biomarkerData.biomarkers).find(
        value => value.code === selectedBiomarkerCode,
      );
      let rangeHumanity = '';
      if (biomarker?.range) {
        if (!biomarker.range.from) {
          rangeHumanity = `<${biomarker.range.to}`;
        } else if (!biomarker.range.to) {
          rangeHumanity = `>${biomarker.range.from}`;
        } else {
          rangeHumanity = `${biomarker.range.from} - ${biomarker.range.to}`;
        }
      }
      if (biomarker) {
        const newDataRow = {
          biomarker: biomarker.name + ` (${biomarker.code})`,
          unitHumanity: biomarker.unit,
          rangeHumanity: rangeHumanity,
          value: '',
          unitLaboratory: '',
          rangeLaboratory: {
            from: '',
            to: '',
          },
          isValid: false,
        };
        setData(prevData => [...prevData, newDataRow]);
        setSelectedBiomarker('');
        setIsUnitLaboratoryValid(false);
        setIsValueValid(false);
        setIsFromValid(false);
        setIsToValid(false);
      } else {
        console.error(`Biomarker with code "${selectedBiomarkerCode}" not found.`);
      }
    }
  };

  useMemo(() => {
    setData(
      userSubmittedLabResult.items
        .map(result => {
          if (!result.code || !biomarkerData.biomarkers[result.code]) {
            console.error(`Biomarker code not found for result: ${JSON.stringify(result)}`);
            return null;
          }
          const biomarker = biomarkerData.biomarkers[result.code];

          const unitHumanity = biomarker.unit;

          let rangeHumanity = '';
          if (biomarker.range) {
            if (!biomarker.range.from) {
              rangeHumanity = `<${biomarker.range.to}`;
            } else if (!biomarker.range.to) {
              rangeHumanity = `>${biomarker.range.from}`;
            } else {
              rangeHumanity = `${biomarker.range.from} - ${biomarker.range.to}`;
            }
          }

          const rangeLaboratory = {
            from: result.range.from,
            to: result.range.to,
          };

          return {
            biomarker: biomarker.name + ` (${result.code})`,
            unitHumanity,
            rangeHumanity,
            value: result.value,
            unitLaboratory: result.unit,
            rangeLaboratory,
          };
        })
        .filter(Boolean),
    );
  }, [biomarkerData, userSubmittedLabResult, classes.dropdown]);

  const { getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    columns,
    data,
  });

  const getBiomarkerOptions = () => {
    const allBiomarkers = Object.values(biomarkerData.biomarkers).map(biomarker => biomarker);
    const dropdownOptions = allBiomarkers.filter(
      biomarker => !data.some(row => row.biomarker.includes(biomarker.name)),
    );

    const biomarkerOptions = Object.values(dropdownOptions).map(biomarker => {
      const alternateNames = biomarker.alternateNames.join(', ');
      return {
        value: biomarker.name,
        label: `${biomarker.name} (${biomarker.code}) [${alternateNames}] `,
      };
    });
    return biomarkerOptions;
  };

  const currentValue = {
    value: selectedBiomarker,
    label: selectedBiomarker,
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '15px' }}>
        <Button onClick={handleOpenPDF} className={classes.pdfButton}>
          Open PDF Results
        </Button>
      </div>
      <UserSubmittedLabResultHeader
        userSubmittedLabResult={userSubmittedLabResult}
        laboratoryName={laboratoryName}
        setLaboratoryName={setLaboratoryName}
        sampleDate={sampleDate}
        setSampleDate={setSampleDate}
        formatDate={formatDate}
        setIsLabNameValid={setIsLabNameValid}
        setIsDateValid={setIsDateValid}
        isLabNameValid={isLabNameValid}
        isDateValid={isDateValid}
      />
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <table className={classes.table}>
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, columnIndex) => (
                  <th className={classes.header} {...column.getHeaderProps()} key={columnIndex}>
                    {column.render('Header')}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row, rowIndex) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()} key={rowIndex}>
                  {row.cells.map((cell, cellIndex) => {
                    if (cell.column.id === 'biomarker') {
                      return (
                        <td className={classes.cell} {...cell.getCellProps()} key={cellIndex}>
                          <Tooltip
                            title={
                              <span style={{ whiteSpace: 'pre-line' }}>
                                {getTooltipContent(cell.row.original.biomarker)}
                              </span>
                            }
                            arrow
                          >
                            <span>{cell.render('Cell')}</span>
                          </Tooltip>
                        </td>
                      );
                    } else {
                      return (
                        <td className={classes.cell} {...cell.getCellProps()} key={cellIndex}>
                          {cell.render('Cell') !== '' && cell.render('Cell')}
                        </td>
                      );
                    }
                  })}
                </tr>
              );
            })}
            <tr>
              <td colSpan={columns.length - 3} className={classes.cell}>
                <Select
                  options={getBiomarkerOptions()}
                  value={selectedBiomarker ? currentValue : null}
                  onChange={selectedOption => {
                    setSelectedBiomarker(selectedOption?.label ?? '');
                    handleAddRowOnSelect(selectedOption?.label ?? '');
                  }}
                  placeholder="Select Biomarker..."
                  isSearchable
                  isDisabled={
                    userSubmittedLabResult?.status === 'completed' ||
                    userSubmittedLabResult?.status === 'rejected'
                  }
                />
              </td>
              <td className={classes.cell}> </td>
              <td className={classes.cell}> </td>
              <td className={classes.cell}> </td>
            </tr>
          </tbody>
        </table>
        <div
          style={{
            position: 'absolute',
            bottom: '-75px',
            right: 0,
            display: 'flex',
            alignItems: 'center',
            margin: '10px',
          }}
        >
          <UserSubmittedLabResultRejectDialog
            biomarkerData={biomarkerData}
            userSubmittedLabResult={userSubmittedLabResult}
            handleSave={handleSave}
            formSubmittedByIsLoggedIn={formSubmittedByIsLoggedIn}
          />
          {status !== 'completed' && status !== 'rejected' && !formSubmittedByIsLoggedIn && (
            <Button
              onClick={async () => {
                await handleSave();
              }}
              className={classes.saveButton}
              variant="outlined"
            >
              Save
            </Button>
          )}
          {status !== 'completed' &&
            status !== 'rejected' &&
            status === 'inProgress' &&
            canSubmitForReview &&
            !formSubmittedByIsLoggedIn && (
              <Button
                onClick={handleConfirmSubmit}
                className={classes.submitButton}
                disabled={!isFormValid}
                variant="outlined"
              >
                Submit for Review
              </Button>
            )}
          {status !== 'completed' &&
            status !== 'rejected' &&
            status === 'inReview' &&
            canComplete && (
              <Button
                onClick={handleConfirmComplete}
                className={classes.completeButton}
                disabled={!isFormValid}
                variant="outlined"
              >
                Complete
              </Button>
            )}
          {(formSubmittedByIsLoggedIn || status === 'completed' || status === 'rejected') && (
            <Button
              onClick={() => {
                history.goBack();
              }}
              className={classes.backButton}
              variant="outlined"
            >
              Back
            </Button>
          )}
        </div>
      </div>
      <UserSubmittedLabResultConfirmDialog
        open={submitConfirmDialogOpen}
        title="Submit for Review"
        content="Are you sure you want to submit the result for Review? You will no longer be able to edit the result."
        onCancel={() => setSubmitConfirmDialogOpen(false)}
        onConfirm={async () => {
          setSubmitConfirmDialogOpen(false);
          await handleSave('forReview');
        }}
      />
      <UserSubmittedLabResultConfirmDialog
        open={completeConfirmDialogOpen}
        title="Complete the Result"
        content="Are you sure you want to Complete the result? This will finalize the result and the user will be notified."
        onCancel={() => setCompleteConfirmDialogOpen(false)}
        onConfirm={async () => {
          setCompleteConfirmDialogOpen(false);
          await handleSave('completed');
        }}
      />
      <UserSubmittedLabResultConfirmDialog
        open={showDeleteConfirmation}
        title="Delete Row"
        content={`Are you sure you want to delete the Biomarker "${biomarkerNameToDelete}"?`}
        onCancel={() => setShowDeleteConfirmation(false)}
        onConfirm={() => {
          if (rowToDeleteIndex !== null) {
            confirmDeleteRow(rowToDeleteIndex);
          }
          setShowDeleteConfirmation(false);
        }}
      />
    </div>
  );
};

const EditableCell: React.FC<any> = ({
  cell,
  row,
  setIsFromValid,
  setIsToValid,
  setIsValueValid,
  biomarkerData,
}) => {
  const [fromValue, setFromValue] = useState(cell.value?.from ?? '');
  const [toValue, setToValue] = useState(cell.value?.to ?? '');
  const [value, setValue] = useState(cell.value ?? '');
  const [isLowValue, setIsLowValue] = useState(false);
  const [isHighValue, setIsHighValue] = useState(false);
  const [tempFromValid, setTempFromValid] = useState(true);
  const [tempToValid, setTempToValid] = useState(true);
  const [tempIsValueValid, setTempIsValueValid] = useState(true);
  const userSubmittedLabResult = useSelector(selectCurrentUserSubmittedLabResult);
  const lowValueTooltip =
    'The Value entered is very low. Please double check the unit in the Lab Result.';
  const highValueTooltip =
    'The Value entered is very high. Please double check the unit in the Lab Result.';

  useEffect(() => {
    if (row) {
      if (cell.column.id === 'rangeLaboratory') {
        setFromValue(cell.value?.from ?? '');
        setToValue(cell.value?.to ?? '');
      } else {
        setValue(cell.value ?? '');
      }
    }
  }, [row, cell.column.id, cell.value]);

  useEffect(() => {
    if (row.original.unitLaboratory) {
      const numericValue = parseFloat(value);
      const biomarkerCode = row.original.biomarker
        .match(/\(([^()]*?)\)(?![^\[]*\])/g)
        ?.pop()
        ?.slice(1, -1);
      const biomarker = biomarkerData.biomarkers[biomarkerCode];
      const multiplier = biomarker.allUnits.find((unitData: any) => {
        return (
          unitData.unit ===
          (row.original.unitLaboratory.props
            ? row.original.unitLaboratory.props.value
            : row.original.unitLaboratory)
        );
      }).multiplier;

      if (numericValue < biomarker?.warningRange?.from / multiplier) {
        setIsLowValue(true);
      } else {
        setIsLowValue(false);
      }

      if (numericValue > biomarker?.warningRange?.to / multiplier) {
        setIsHighValue(true);
      } else {
        setIsHighValue(false);
      }
    }
  }, [value, row.original.unitLaboratory, biomarkerData, row.original.biomarker]);

  useEffect(() => {
    setIsValueValid(tempIsValueValid);
  }, [tempIsValueValid, setIsValueValid]);

  useEffect(() => {
    if (tempIsValueValid && tempFromValid && tempToValid && row.original.unitLaboratory) {
      row.original.isValid = true;
    }
  }, [row, tempFromValid, tempToValid, tempIsValueValid]);

  useEffect(() => {
    if (
      userSubmittedLabResult?.status === 'inReview' ||
      userSubmittedLabResult?.status === 'inProgress'
    ) {
      if (/^[0-9]+([.][0-9]+)?$/.test(row.original.value)) {
        setTempIsValueValid(true);
      } else {
        setTempIsValueValid(false);
      }
    }
  }, [tempIsValueValid, userSubmittedLabResult, row.original.value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (cell.column.id === 'rangeLaboratory') {
      if (name === 'from') {
        setFromValue(value);
        row.original.rangeLaboratory.from = value;
      } else if (name === 'to') {
        setToValue(value);
        row.original.rangeLaboratory.to = value;
      }
    } else {
      setValue(value);
      row.original.value = value;
    }

    if (isNaN(row.original.rangeLaboratory.from)) {
      setIsFromValid(false);
      setTempFromValid(false);
    } else {
      setIsFromValid(true);
      setTempFromValid(true);
    }
    if (isNaN(row.original.rangeLaboratory.to)) {
      setTempToValid(false);
      setIsToValid(false);
    } else {
      setTempToValid(true);
      setIsToValid(true);
    }

    if (/^[0-9]+([.][0-9]+)?$/.test(row.original.value)) {
      setIsValueValid(true);
      setTempIsValueValid(true);
    } else {
      setIsValueValid(false);
      setTempIsValueValid(false);
    }
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      {cell.column.id === 'rangeLaboratory' ? (
        <>
          <TextField
            type="text"
            name="from"
            size="small"
            variant="outlined"
            value={fromValue}
            onChange={handleChange}
            error={!tempFromValid}
            disabled={
              userSubmittedLabResult?.status === 'completed' ||
              userSubmittedLabResult?.status === 'rejected'
            }
          />
          <div style={{ margin: '10px' }}> - </div>
          <TextField
            type="text"
            name="to"
            size="small"
            variant="outlined"
            value={toValue}
            onChange={handleChange}
            error={!tempToValid}
            disabled={
              userSubmittedLabResult?.status === 'completed' ||
              userSubmittedLabResult?.status === 'rejected'
            }
          />
        </>
      ) : (
        <Tooltip title={(isLowValue && lowValueTooltip) || (isHighValue && highValueTooltip) || ''}>
          <TextField
            type="text"
            name="value"
            value={value}
            onChange={handleChange}
            error={!tempIsValueValid}
            required
            size="small"
            variant="outlined"
            style={{
              backgroundColor: isLowValue || isHighValue ? 'yellow' : 'transparent',
            }}
            disabled={
              userSubmittedLabResult?.status === 'completed' ||
              userSubmittedLabResult?.status === 'rejected'
            }
            autoFocus
          />
        </Tooltip>
      )}
    </div>
  );
};

export default UserSubmittedLabResultsTable;
